import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import RoomIcon from '@mui/icons-material/Room';
import BluetoothSearchingIcon from '@mui/icons-material/BluetoothSearching';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const DRAWER_WIDTH = 240;

// Menu lateral. Em desktop (md+) fica permanente. Em mobile (xs/sm) fica
// temporário — abre com o hamburger da NavBar e fecha ao clicar num item
// ou no overlay.
export default function LeftDrawer({ mobileOpen, onMobileClose }) {
  const navigate = useNavigate();

  // Itens do menu computados diretamente do storage — sem setState acumulador
  // (evita duplicação por StrictMode / re-render).
  const items = useMemo(() => {
    const base = [
      {
        id: 1, text: 'Locais', icon: <RoomIcon />, route: '/home',
      },
      {
        id: 2, text: 'Beacons', icon: <BluetoothSearchingIcon />, route: '/beacons',
      },
    ];
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      if (user?.role === 'administrator') {
        base.push({
          id: 3, text: 'Usuários', icon: <AccountCircleIcon />, route: '/users',
        });
      }
    } catch { /* localStorage inválido — ignora */ }
    return base;
  }, []);

  const handleNavigate = (route) => {
    navigate(route);
    if (onMobileClose) onMobileClose();
  };

  const menuContent = (
    <>
      <Toolbar />
      <Divider />
      <List>
        {items.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton onClick={() => handleNavigate(item.route)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
    </>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      aria-label="Menu principal"
    >
      {/* Drawer temporário — mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH },
        }}
      >
        {menuContent}
      </Drawer>

      {/* Drawer permanente — desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
        open
      >
        {menuContent}
      </Drawer>
    </Box>
  );
}

LeftDrawer.propTypes = {
  mobileOpen: PropTypes.bool.isRequired,
  onMobileClose: PropTypes.func.isRequired,
};
